import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';

// Intentionally NOT built on the shared `<Tooltip>` primitive.
// `<Tooltip>` is a react-aria hover popover that wraps a trigger element and
// owns positioning, delay, and animation. This component is passed as
// `content` to recharts' `<Tooltip>` — recharts positions its own overlay
// based on cursor proximity to a data point, so we only need the styled panel
// (title + stacked per-series rows), not a trigger-popover pair.

export interface IChartSeriesTooltipProps {
  /**
   * Header label — typically the hovered period / day. Rendered small, semibold, in a muted color.
   */
  title: React.ReactNode;
  /**
   * Text transform for the title. Defaults to `normal`. Use `uppercase` for day/period labels
   * that read as eyebrow-style titles.
   */
  titleTransform?: 'normal' | 'uppercase';
  /**
   * Tooltip rows, typically `<ChartSeriesTooltipRow>` elements.
   */
  children: React.ReactNode;
}

/**
 * Dark-background tooltip container for recharts `content` props. Pairs a small muted
 * title with a stack of `<ChartSeriesTooltipRow>` rows for per-series values. Matches
 * the Figma tooltip spec used by every non-trivial chart.
 */
export const ChartSeriesTooltip = ({ title, titleTransform = 'normal', children }: IChartSeriesTooltipProps) => (
  <div className="bg-primary-solid flex flex-col gap-0.5 rounded-lg px-3 py-2 shadow-lg">
    <p className={cx('text-utility-gray-300 text-[9px] font-semibold', titleTransform === 'uppercase' && 'uppercase')}>
      {title}
    </p>
    <div className="flex flex-col gap-0.5">{children}</div>
  </div>
);

export interface IChartSeriesTooltipRowProps {
  /**
   * Optional colored swatch shown left of the label. Pass either `<ChartSeriesSolidLineSwatch>`,
   * `<ChartSeriesDottedLineSwatch>`, `<ChartSeriesSquareSwatch>`, or any custom element.
   * Leave undefined for rows without a swatch (e.g. a "Net amount" summary row).
   */
  swatch?: React.ReactNode;
  label: React.ReactNode;
  value: React.ReactNode;
  /**
   * Additional classes applied to the value element (e.g. `text-utility-success-500`
   * for a colored summary value).
   */
  valueClassName?: string;
}

/**
 * A single `{swatch, label, value}` row inside a `<ChartSeriesTooltip>`.
 */
export const ChartSeriesTooltipRow = ({ swatch, label, value, valueClassName }: IChartSeriesTooltipRowProps) => (
  <div className="flex items-center justify-between gap-4">
    <div className="flex items-center gap-1.5">
      {swatch}
      <p className="text-tooltip-supporting-text text-xs font-medium">{label}</p>
    </div>
    <p className={cx('text-tooltip-supporting-text text-right text-xs font-semibold', valueClassName)}>{value}</p>
  </div>
);

/**
 * Faint horizontal divider between rows — use above summary rows like "Net amount".
 */
export const ChartSeriesTooltipDivider = () => <div className="my-0.5 border-t border-white/20" />;

export interface IChartSeriesSquareSwatchProps {
  /**
   * Background-color class (e.g. `bg-utility-indigo-500`).
   */
  className: string;
}

/**
 * Small filled square swatch for categorical series.
 */
export const ChartSeriesSquareSwatch = ({ className }: IChartSeriesSquareSwatchProps) => (
  <div className={cx('h-1.5 w-1.5', className)} />
);

export interface IChartSeriesLineSwatchProps {
  /**
   * Text-color class that sets `currentColor` for the line stroke (e.g. `text-utility-indigo-500`).
   */
  className: string;
  /**
   * Line style. `solid` renders a continuous stroke; `dotted` renders round dots on an 8px pitch.
   */
  style?: 'solid' | 'dotted';
}

/**
 * Small SVG line swatch for continuous-series tooltips (line + area charts).
 */
export const ChartSeriesLineSwatch = ({ className, style = 'solid' }: IChartSeriesLineSwatchProps) => (
  <svg width="12" height="4" className={className}>
    {style === 'dotted' ? (
      <line
        x1="0"
        y1="2"
        x2="12"
        y2="2"
        stroke="currentColor"
        strokeWidth="2"
        strokeDasharray="0 4"
        strokeLinecap="round"
      />
    ) : (
      <line x1="0" y1="2" x2="12" y2="2" stroke="currentColor" strokeWidth="2" />
    )}
  </svg>
);
